import first from 'lodash/first'
import React, { FC, useContext } from 'react'
import styled from 'styled-components'

import { ImageV2 } from '@Components/Image'
import { Link } from '@Components/Link'
import { NavigationEvent } from '@Events/Navigation'
import { Page } from '@Generated/graphql'
import { MetadataContext } from '@Globals/Metadata'
import { useTranslation } from '@Globals/Translations'

interface CMSPostProps {
  post: Page
  title: string
  type: 'articles' | 'collections'
}

const StyledTitle = styled.span`
  display: block;
  color: ${({ theme }) => theme.colors.grey};
  text-transform: uppercase;
  margin-bottom: 5px;
`

const StyledCollectionItemContainer = styled(Link)`
  width: 100%;
`

const StyledDescription = styled.span`
  display: block;
  color: ${({ theme }) => theme.colors.greyLight};
  margin-top: 5px;
  width: 100%;
  font-size: 0.75rem;
  letter-spacing: 0.5px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const StyledWrapper = styled.div`
  background-color: ${({ theme }) => theme.colors.greyLightest};
  padding: 20px 10px;
  border-bottom: 1px solid #ddd;
  min-height: 200px;
`

const StyledMagazineItemWrapper = styled.div`
  width: 100%;
  border-bottom: 1px dashed #d2d2d2;
  padding-bottom: 15px;
  margin-bottom: 20px;
`

const StyledAllCollectionsLink = styled(Link)`
  display: block;
  margin-top: 10px;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.primary};
`

const StyledMagazineItemWrapperLastItem = styled.div`
  border-bottom: none;
  margin-bottom: 0;
  padding-bottom: 0;
`

const CMSPost: FC<CMSPostProps> = ({ post, title, type }: CMSPostProps) => (
  <>
    <StyledTitle>{title}</StyledTitle>
    <StyledCollectionItemContainer
      event={new NavigationEvent({
        attributes: {
          dest_page_type: type,
          dest_page_type_id: post.id || '',
          source_id: 'mobile_menu',
        },
        eventName: 'nav_link_click',
      })}
      href={post.url || ''}
    >
      <ImageV2
        key={post.id}
        cropRatio="360:216"
        height="216"
        src={post.image?.url || ''}
        width="360"
      />
      <StyledDescription>{post.title}</StyledDescription>
    </StyledCollectionItemContainer>
  </>
)

export const CMSPosts: FC = () => {
  const { t } = useTranslation()
  const { cmsData, storeURL } = useContext(MetadataContext)

  const article = cmsData?.latestArticle
  const collection = first(cmsData?.latestCollections)

  return (
    <StyledWrapper>
      <StyledMagazineItemWrapper>
        {collection && (
          <CMSPost
            post={collection}
            title={t('header:NEW_COLLECTIONS_LABEL')}
            type="collections"
          />
        )}
        <StyledAllCollectionsLink
          event={new NavigationEvent({
            attributes: {
              dest_page_type: 'cms_category_collections',
              dest_page_type_id: '',
              source_id: 'mobile_menu',
            },
            eventName: 'nav_link_click',
          })}
          href={storeURL('collections')}
        >
          {t('header:VIEW_ALL_COLLECTIONS_LABEL')}
        </StyledAllCollectionsLink>
      </StyledMagazineItemWrapper>
      <StyledMagazineItemWrapperLastItem>
        {article && (
          <CMSPost
            post={article}
            title={t('header:NEW_ARTICLES_LABEL')}
            type="articles"
          />
        )}
        <StyledAllCollectionsLink
          event={new NavigationEvent({
            attributes: {
              dest_page_type: 'cms_category_articles',
              dest_page_type_id: '',
              source_id: 'mobile_menu',
            },
            eventName: 'nav_link_click',
          })}
          href={storeURL('articles')}
        >
          {t('header:VIEW_ALL_ARTICLES_LABEL')}
        </StyledAllCollectionsLink>
      </StyledMagazineItemWrapperLastItem>
    </StyledWrapper>
  )
}
